import { csrfFetch, refreshCSRFToken, isCSRFTokenError } from "../utils/csrfFetch";
import Cookies from "js-cookie";

// Action Types
const LOAD_TRAILERS = 'trailers/LOAD_TRAILERS';
const ADD_TRAILER = 'trailers/ADD_TRAILER';
const DELETE_TRAILER = 'trailers/DELETE_TRAILER';
const UPDATE_TRAILER = 'trailers/UPDATE_TRAILER';
const GET_TRAILER = 'trailers/GET_TRAILER'
const PATCH_TRAILER = 'trailers/PATCH_TRAILER'
const GET_TRAILERS = 'trailers/GET_TRAILERS';
const GET_AVAILABLE_TRAILERS = 'trailers/GET_AVAILABLE_TRAILERS';
const BULK_UPLOAD_TRAILERS = 'trailers/BULK_UPLOAD_TRAILERS';
const BULK_UPLOAD_START = 'trailers/BULK_UPLOAD_START';
const BULK_UPLOAD_SUCCESS = 'trailers/BULK_UPLOAD_SUCCESS';
const BULK_UPLOAD_FAILURE = 'trailers/BULK_UPLOAD_FAILURE';

// Action Creators
export const loadTrailers = (trailers) => ({
  type: LOAD_TRAILERS,
  trailers
});

const addTrailerAction = (trailer) => ({
  type: ADD_TRAILER,
  trailer
});

const updateTrailerAction = (trailer) => ({
  type: UPDATE_TRAILER,
  trailer
});

const deleteTrailerAction = (trailerId) => ({
  type: DELETE_TRAILER,
  trailerId
});

export const get = (trailer) => ({
  type:GET_TRAILER,
  trailer
})

export const patch = (trailer) => ({
  type : PATCH_TRAILER,
  trailer
})

const getTrailersAction = (trailers) => ({
    type: GET_TRAILERS,
    trailers
});

const getAvailableTrailersAction = (trailers) => ({
    type: GET_AVAILABLE_TRAILERS,
    trailers
});

const bulkUploadStartAction = () => ({
  type: BULK_UPLOAD_START
});

const bulkUploadSuccessAction = (results) => ({
  type: BULK_UPLOAD_SUCCESS,
  results
});

const bulkUploadFailureAction = (error) => ({
  type: BULK_UPLOAD_FAILURE,
  error
});

// Thunks
export const fetchTrailersAdmin = () => async (dispatch) => {
  const response = await csrfFetch('/api/trailers/');
  if (response.ok) {
    const trailers = await response.json();
    dispatch(loadTrailers(trailers));
    return trailers;
  }
};

export const fetchTrailersUser = (accountId) => async (dispatch) => {
  try {
    // Add timestamp for cache busting
    const timestamp = new Date().getTime();

    let url = '/api/trailers/';
    if (accountId) {
      url += `?account_id=${accountId}&_t=${timestamp}`;
    } else {
      url += `?_t=${timestamp}`;
    }

    const response = await csrfFetch(url, {
      headers: {
        'Cache-Control': 'no-cache'
      }
    });

    if (response.ok) {
      const trailers = await response.json();
      dispatch(loadTrailers(trailers));
      return trailers;
    } else {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch trailers');
    }
  } catch (error) {
    console.error('Error fetching trailers:', error);
    throw error;
  }
};

export const getTrailerById = (id) => async (dispatch) => {
  try {
    const response = await csrfFetch(`/api/trailers/${id}`);
    if (response.ok) {
      const trailer = await response.json();
      dispatch(get(trailer));
      return trailer;
    } else {
      const errorData = await response.json();
      console.error('Error fetching trailer:', errorData);
      return null;
    }
  } catch (error) {
    console.error('Exception in getTrailerById:', error);
    return null;
  }
};

export const addTrailer = (trailer) => async (dispatch) => {
  const response = await csrfFetch('/api/trailers/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(trailer)
  });

  if (response.ok) {
    const newTrailer = await response.json();
    dispatch(addTrailerAction(newTrailer));
    return newTrailer;
  }
};

export const deleteTrailer = (trailerId) => async (dispatch) => {
  const response = await csrfFetch(`/api/trailers/${trailerId}`, {
    method: 'DELETE'
  });

  if (response.ok) {
    dispatch(deleteTrailerAction(trailerId));
    return true;
  }
};

export const updateTrailer = (trailer) => async (dispatch) => {
  try {
    // Try to get the CSRF token before making the request
    const token = Cookies.get('csrf_token');
    if (!token) {
      await refreshCSRFToken();
    }

    const response = await csrfFetch(`/api/trailers/${trailer.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(trailer)
    });

    const updatedTrailer = await response.json();
    if (updatedTrailer.errors) {
      throw new Error(updatedTrailer.errors[0] || 'Failed to update trailer');
    }

    dispatch(updateTrailerAction(updatedTrailer));
    return updatedTrailer;
  } catch (error) {
    console.error('Error updating trailer:', error);
    if (isCSRFTokenError(error)) {
      // If it's a CSRF error, try one more time after clearing the token
      try {
        Cookies.remove('csrf_token');
        await refreshCSRFToken();

        const response = await csrfFetch(`/api/trailers/${trailer.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(trailer)
        });

        const updatedTrailer = await response.json();
        if (updatedTrailer.errors) {
          throw new Error(updatedTrailer.errors[0] || 'Failed to update trailer');
        }

        dispatch(updateTrailerAction(updatedTrailer));
        return updatedTrailer;
      } catch (retryError) {
        console.error('Error retrying trailer update:', retryError);
        throw retryError;
      }
    }
    throw error;
  }
};

export const patchTrailer = (data) => async(dispatch) => {
  try {
    // Try to get the CSRF token before making the request
    const token = Cookies.get('csrf_token');
    if (!token) {
      await refreshCSRFToken();
    }

    const response = await csrfFetch(`/api/trailers/${data.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    });

    const trailer = await response.json();
    if (trailer.errors) {
      throw new Error(trailer.errors[0] || 'Failed to patch trailer');
    }

    dispatch(patch(trailer));
    return trailer;
  } catch (error) {
    console.error('Error patching trailer:', error);
    if (isCSRFTokenError(error)) {
      // If it's a CSRF error, try one more time after clearing the token
      try {
        Cookies.remove('csrf_token');
        await refreshCSRFToken();

        const response = await csrfFetch(`/api/trailers/${data.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(data)
        });

        const trailer = await response.json();
        if (trailer.errors) {
          throw new Error(trailer.errors[0] || 'Failed to patch trailer');
        }

        dispatch(patch(trailer));
        return trailer;
      } catch (retryError) {
        console.error('Error retrying trailer patch:', retryError);
        throw retryError;
      }
    }
    throw error;
  }
};

export const getTrailers = () => async (dispatch) => {
    try {
        const response = await csrfFetch('/api/trailers');
        if (response.ok) {
            const trailers = await response.json();
            dispatch(getTrailersAction(trailers));
            return trailers;
        } else {
            const error = await response.json();
            throw new Error(error.error || 'Failed to fetch trailers');
        }
    } catch (error) {
        console.error('Error fetching trailers:', error);
        throw error;
    }
};

export const getAvailableTrailers = (params) => async (dispatch) => {
    try {
        const { location, accountId, excludeRedTagged } = params;

        // Build query string
        let queryString = `/api/moves/available-trailers?location=${encodeURIComponent(location)}&account_id=${accountId}`;

        // Add the exclude red tagged parameter if needed
        if (excludeRedTagged) {
            queryString += `&exclude_red_tagged=true`;
        }

        // Add timestamp for cache busting
        const timestamp = new Date().getTime();
        queryString += `&_t=${timestamp}`;

        const response = await csrfFetch(queryString);
        if (response.ok) {
            const trailers = await response.json();
            dispatch(getAvailableTrailersAction(trailers));
            return trailers;
        } else {
            const error = await response.json();
            throw new Error(error.error || 'Failed to fetch available trailers');
        }
    } catch (error) {
        console.error('Error fetching available trailers:', error);
        throw error;
    }
};

export const bulkUploadTrailers = (formData) => async (dispatch) => {
  dispatch(bulkUploadStartAction());

  try {
    // Make sure we're using fetch directly or csrfFetch without any additional modification to FormData
    const response = await csrfFetch('/api/trailers/bulk-upload', {
      method: 'POST',
      // Don't set Content-Type header - let the browser set it with the boundary parameter
      headers: {
        // Removing Content-Type to let browser set it correctly for FormData with files
      },
      body: formData
    });

    // Check if response is HTML (error page)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      throw new Error('Server error - Received HTML response instead of JSON. Please check that your file format is correct and try again.');
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to upload trailers');
    }

    const results = await response.json();
    dispatch(bulkUploadSuccessAction(results));
    return { success: true, results };
  } catch (error) {
    console.error('Bulk upload error:', error);
    dispatch(bulkUploadFailureAction(error.toString()));
    return { success: false, error: error.message };
  }
};

// Reducer
const initialState = {};

const trailersReducer = (state = initialState, action) => {
  switch (action.type) {
    case LOAD_TRAILERS:
      return action.trailers;
    case ADD_TRAILER:
      return { ...state, [action.trailer.id]: action.trailer };
    case UPDATE_TRAILER:
      return { ...state, [action.trailer.id]: action.trailer };
    case DELETE_TRAILER:
      const newState = { ...state };
      delete newState[action.trailerId];
      return newState;
    case GET_TRAILER:
      return {
        ...state,
        [action.trailer.id]: action.trailer,
      };
    case PATCH_TRAILER: {
      const trailer = action.trailer;
      return {
        ...state,
        [trailer.id]: { ...state[trailer.id], ...trailer },
      };
    }
    case GET_TRAILERS:
      const allTrailers = {};
      action.trailers.forEach(trailer => {
        allTrailers[trailer.id] = trailer;
      });
      return allTrailers;
    case GET_AVAILABLE_TRAILERS:
      const availableTrailers = {};
      action.trailers.forEach(trailer => {
        availableTrailers[trailer.id] = trailer;
      });
      return { ...state, available: availableTrailers };
    case BULK_UPLOAD_START:
      return { ...state, bulkUpload: { status: 'started' } };
    case BULK_UPLOAD_SUCCESS:
      return { ...state, bulkUpload: { status: 'succeeded', results: action.results } };
    case BULK_UPLOAD_FAILURE:
      return { ...state, bulkUpload: { status: 'failed', error: action.error } };
    default:
      return state;
  }
};

export default trailersReducer;
